import { computed } from 'vue';
import { useCurriculumStore } from '@/stores/curriculumStore';
import { useProgressStore } from '@/stores/progressStore';
import { useUserStore } from '@/stores/userStore';
import { useContinueLearningEngine } from '@/composables/useContinueLearningEngine';
import { isLessonLocked, requiredPlanLabel } from '@/composables/usePlanGating';

/**
 * Shared composable for computing journey units from curriculum + progress data.
 * Used by both TrackPage and MountainTrailMap.
 */
const BAND_ORDER = ["beginner", "developing", "secure", "confident", "mastery"];
function bandIndex(b) { return BAND_ORDER.indexOf(b); }

export function useJourneyUnits(childId) {
  const curriculumStore = useCurriculumStore();
  const progressStore = useProgressStore();
  const userStore = useUserStore();
  const engine = useContinueLearningEngine(childId);

  // Check if all topics in a lesson are at least "secure"
  function isLessonReady(lesson) {
    const subs = lesson.subsections || [];
    if (!subs.length) return false;
    for (const sub of subs) {
      const band = progressStore.getSectionGroupBand(childId, sub.content);
      if (bandIndex(band) < bandIndex("secure")) return false;
    }
    return true;
  }

  const currentLesson = computed(() => {
    const suggested = engine.suggestedNextLesson.value;
    if (!suggested?.lessonId) return null;
    return curriculumStore.allLessons.find(l => l.id === suggested.lessonId);
  });

  const journeyUnits = computed(() => {
    if (!curriculumStore.modules?.length) return [];

    // Pre-compute flat lesson list to find the current lesson's position
    const allLessonsFlat = curriculumStore.modules.flatMap(mod =>
      mod.units.flatMap(unit => unit.lessons)
    );
    const currentFlatIdx = allLessonsFlat.findIndex(l => l.id === currentLesson.value?.id);

    const progress = progressStore.getChildProgress(childId);
    let flatIdx = 0;

    return curriculumStore.modules.flatMap(mod =>
      mod.units.map(unit => ({
        id: unit.id,
        title: unit.title,
        lessons: unit.lessons.map(lesson => {
          const myIdx = flatIdx++;
          const locked = isLessonLocked(lesson.excludedPlans, userStore.effectivePlan);

          // A lesson is only "completed" when all its topics have reached secure band.
          // isLessonCompleted (badge) is not sufficient — badges can be awarded at session
          // end even if not all topics were practiced, which would incorrectly mark lessons done.
          const isCompleted = !locked && isLessonReady(lesson);
          const isCurrent = !locked && lesson.id === currentLesson.value?.id;
          // Skipped: not completed, not current, and sits before the current lesson in curriculum order
          const isSkipped = !locked && !isCompleted && !isCurrent && currentFlatIdx > 0 && myIdx < currentFlatIdx;

          let avgAccuracy = 0;
          if (!locked && lesson.subsections?.length) {
            const sum = lesson.subsections.reduce(
              (acc, sub) => acc + (progress[sub.content]?.accuracy || 0),
              0
            );
            avgAccuracy = sum / lesson.subsections.length;
          }
          const stars = avgAccuracy >= 0.85 ? 3 : avgAccuracy >= 0.6 ? 2 : avgAccuracy >= 0.35 ? 1 : 0;

          const requiredPlan = locked ? requiredPlanLabel(lesson.excludedPlans) : null;
          return {
            id: lesson.id,
            title: lesson.title,
            status: locked ? 'locked' : isCompleted ? 'completed' : isCurrent ? 'current' : isSkipped ? 'skipped' : 'available',
            lockMessage: requiredPlan ? `Upgrade to ${requiredPlan} to unlock this lesson` : null,
            stars
          };
        })
      }))
    );
  });

  // Flat array of all lessons in order
  const flatLessons = computed(() => {
    return journeyUnits.value.flatMap(u => u.lessons);
  });

  // Index of current lesson in flatLessons
  const currentLessonIndex = computed(() => {
    return flatLessons.value.findIndex(l => l.status === 'current');
  });

  return {
    journeyUnits,
    flatLessons,
    currentLessonIndex,
    currentLesson,
    engine
  };
}
